import api from "../index";

// ============================================
// TYPES
// ============================================

export interface StreakMilestone {
  day: number;
  credits: number;
  label: string;
  badge: string;
  notification?: string;
}

export interface Achievement {
  id: string;
  name: string;
  description?: string;
  credits: number;
  trigger: {
    type: string;
    threshold?: number;
    metadata?: any;
  };
  oneTime: boolean;
  badge: string;
  enabled: boolean;
}

export interface GamificationPrize {
  label: string;
  credits: number;
  probability: number;
  color: string;
}

export interface GamificationGame {
  type: string;
  enabled: boolean;
  prizes: GamificationPrize[];
  config?: any;
}

export interface CreditConfig {
  _id?: string;
  enabled: boolean;
  dailyCreditCap: number;
  dailyLogin: {
    enabled: boolean;
    credits: number;
    resetHour: number;
  };
  streak: {
    enabled: boolean;
    gracePeriodHours: number;
    milestones: StreakMilestone[];
  };
  achievements: {
    enabled: boolean;
    list: Achievement[];
  };
  gamification: {
    enabled: boolean;
    cooldownHours: number;
    games: GamificationGame[];
  };
  specialEvents?: Array<{
    name: string;
    description: string;
    startDate: Date;
    endDate: Date;
    multiplier: number;
    bonusCredits: number;
    enabled: boolean;
  }>;
  updatedAt?: string;
  updatedBy?: string;
}

export interface CreditAnalyticsFilters {
  startDate?: string;
  endDate?: string;
  groupBy?: string;
}

export interface CreditAnalytics {
  totalCreditsAwarded: number;
  activeStreaks: number;
  averageStreakLength: number;
  engagementRate: number;
  creditsBySource: {
    daily_login: number;
    streak: number;
    gamification: number;
    achievement: number;
    admin_grant: number;
  };
  streakDistribution: {
    range: string;
    count: number;
  }[];
  creditsOverTime: {
    date: string;
    credits: number;
    type?: string;
  }[];
  topAchievements: {
    id: string;
    name: string;
    count: number;
  }[];
}

export interface StreakAnalytics {
  totalActiveStreaks: number;
  averageStreakLength: number;
  longestStreak: number;
  streakDistribution: {
    range: string;
    count: number;
    percentage: number;
  }[];
  milestoneReached: {
    day: number;
    count: number;
  }[];
  streaksOverTime: {
    date: string;
    active: number;
    broken: number;
  }[];
}

export interface AchievementAnalytics {
  totalUnlocked: number;
  mostPopular: {
    id: string;
    name: string;
    count: number;
    credits: number;
  }[];
  unlocksOverTime: {
    date: string;
    count: number;
  }[];
  byCategory: {
    category: string;
    count: number;
  }[];
}

export interface LeaderboardEntry {
  rank: number;
  userId: string;
  userName: string;
  email: string;
  avatar?: string;
  totalCredits: number;
  streakDays: number;
  achievementsCount: number;
  lastActive?: string;
}

export interface GrantCreditsRequest {
  userIds: string[];
  amount: number;
  reason: string;
}

// ============================================
// API FUNCTIONS
// ============================================

/**
 * Get credit system configuration
 */
export const getCreditConfig = async () => {
  return await api.get("/credits/config");
};

/**
 * Update credit system configuration
 */
export const updateCreditConfig = async (config: Partial<CreditConfig>) => {
  return await api.put("/credits/config", config);
};

/**
 * Get credit analytics with date filters
 */
export const getCreditAnalytics = async (startDate?: string, endDate?: string) => {
  const params = new URLSearchParams();

  if (startDate) params.append("startDate", startDate);
  if (endDate) params.append("endDate", endDate);

  const queryString = params.toString();
  return await api.get(`/credits/analytics${queryString ? `?${queryString}` : ""}`);
};

/**
 * Get streak analytics
 */
export const getStreakAnalytics = async () => {
  return await api.get("/credits/analytics/streaks");
};

/**
 * Get achievement analytics
 */
export const getAchievementAnalytics = async () => {
  return await api.get("/credits/analytics/achievements");
};

/**
 * Grant credits to users manually
 */
export const grantCredits = async (userIds: string[], amount: number, reason: string) => {
  return await api.post("/credits/grant", { userIds, amount, reason });
};

/**
 * Get credit leaderboard
 */
export const getCreditLeaderboard = async (limit: number = 50, sortBy: string = "totalCredits") => {
  const params = new URLSearchParams();
  params.append("limit", limit.toString());
  params.append("sortBy", sortBy);

  return await api.get(`/credits/leaderboard?${params.toString()}`);
};

/**
 * Export credit analytics data
 */
export const exportCreditAnalytics = async (
  format: "csv" | "json",
  startDate?: string,
  endDate?: string
) => {
  const params = new URLSearchParams();
  params.append("format", format);

  if (startDate) params.append("startDate", startDate);
  if (endDate) params.append("endDate", endDate);

  return await api.get(`/credits/analytics/export?${params.toString()}`, {
    responseType: format === "csv" ? "blob" : "json",
  });
};

/**
 * Get user credit details
 */
export const getUserCreditDetails = async (userId: string) => {
  return await api.get(`/credits/user/${userId}`);
};

/**
 * Get credit transaction history for a user
 */
export const getUserCreditHistory = async (
  userId: string,
  page: number = 1,
  perPage: number = 20
) => {
  const params = new URLSearchParams();
  params.append("page", page.toString());
  params.append("perPage", perPage.toString());

  return await api.get(`/credits/user/${userId}/history?${params.toString()}`);
};

/**
 * Search users for granting credits
 */
export const searchUsersForCredits = async (query: string, limit: number = 20) => {
  const params = new URLSearchParams();
  params.append("q", query);
  params.append("limit", limit.toString());

  return await api.get(`/credits/users/search?${params.toString()}`);
};
